/**
 * This is the entry point for Feature Hub App integration
 */

import React from 'react';
import type {
  FeatureAppDefinition,
  FeatureAppEnvironment,
  FeatureServices,
  Logger,
} from '@feature-hub/core';
import type { ReactFeatureApp } from '@feature-hub/react';

import type { GfaLocaleServiceV1 } from '@volkswagen-onehub/gfa-locale-service';
import type { I18NServiceV1 } from '@oneaudi/i18n-service';
import type { LocaleServiceConfig } from '@volkswagen-onehub/locale-service';

import App from './FeatureApp';
import I18nContextComponent from '@oneaudi/i18n-context';
import { ThemeProvider } from '@oneaudi/unified-web-common';
import { getDesignLibrary } from '../alpha-helper';

export interface FeatureServiceDependencies extends FeatureServices {
  readonly 's2:logger'?: Logger;
  readonly 'gfa:locale-service': GfaLocaleServiceV1;
  readonly 'dbad:audi-i18n-service': I18NServiceV1;
  readonly 'locale-service': LocaleServiceConfig;
}

const featureAppDefinition: FeatureAppDefinition<ReactFeatureApp, FeatureServiceDependencies> = {
  dependencies: {
    featureServices: {
      'dbad:audi-i18n-service': '^1.0.0',
      'locale-service': '^1.0.0',
      's2:logger': '^1.0.0',
      'gfa:locale-service': '^1.0.0',
    },
    externals: {
      react: '*',
      'styled-components': '*',
      ...getDesignLibrary({ unifiedWebCommonVersion: '^1.8.0' }),
    },
  },

  create: ({ featureServices }: FeatureAppEnvironment<FeatureServiceDependencies, void>) => {
    const loggerService = featureServices['s2:logger'];
    loggerService?.info('Feature App created.');

    const i18nData: I18NServiceV1 = featureServices['dbad:audi-i18n-service'];
    const localeServiceData = featureServices['locale-service'];

    return {
      render: (): React.ReactNode => {
        return (
          <ThemeProvider>
            <I18nContextComponent
              language={localeServiceData.language}
              i18nData={i18nData}
              scopes={[]}
            >
              <App />
            </I18nContextComponent>
          </ThemeProvider>
        );
      },
    };
  },
};

export default featureAppDefinition;
